import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Switch,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { superAdminService } from '../../Services/SuperAdminService';
import AddLabDialog from './AddLabDialog';
import '../../UI/Common.css';
import './SuperAdmin.css';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const FILTERS = [
  { key: 'all', label: 'All labs' },
  { key: 'active', label: 'Active' },
  { key: 'overdue', label: 'Payment overdue' },
  { key: 'pending', label: 'Payments to verify' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'nosub', label: 'No subscription' }
];

const formatAmount = (value) =>
  `${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} PKR`;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

// Overdue only the day AFTER the due date, matching BillingService.checkBillingDue.
const isOverdue = (lab) => {
  if (!lab.subscription?.nextDueDate) return false;
  const due = new Date(lab.subscription.nextDueDate);
  due.setHours(0, 0, 0, 0);
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()) > due;
};

function StatusPill({ tone, children }) {
  return <span className={`sa-pill sa-pill-${tone}`}>{children}</span>;
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  // Lab ids with a toggle/suspension request in flight.
  const [busyIds, setBusyIds] = useState([]);

  const [suspendTarget, setSuspendTarget] = useState(null);
  const [showAddLab, setShowAddLab] = useState(false);

  // Id rather than a snapshot so the dialog's summary (next due date,
  // totals) reflects the refreshed list after a payment is verified.
  const [paymentsLabId, setPaymentsLabId] = useState(null);
  const paymentsLab = labs.find((l) => l.laboratoryId === paymentsLabId) || null;
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [busyPaymentId, setBusyPaymentId] = useState(null);

  const fetchLabs = async () => {
    try {
      setLoading(true);
      setLabs(await superAdminService.getLabs());
    } catch (error) {
      console.error('Failed to load labs:', error);
      toast.error('Failed to load laboratories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabs();
  }, []);

  const setBusy = (id, busy) =>
    setBusyIds((prev) => (busy ? [...prev, id] : prev.filter((x) => x !== id)));

  const updateLab = (id, changes) =>
    setLabs((prev) => prev.map((l) => (l.laboratoryId === id ? { ...l, ...changes } : l)));

  const stats = useMemo(() => ({
    total: labs.length,
    active: labs.filter((l) => !l.isSuspended && l.subscription?.status === 'Active').length,
    overdue: labs.filter((l) => !l.isSuspended && isOverdue(l)).length,
    pending: labs.reduce((sum, l) => sum + l.pendingPaymentCount, 0),
    suspended: labs.filter((l) => l.isSuspended).length
  }), [labs]);

  const visibleLabs = useMemo(() => {
    const term = search.trim().toLowerCase();
    return labs.filter((l) => {
      if (term) {
        const haystack = [l.laboratoryName, l.laboratoryEmail, l.laboratoryContactNumber, String(l.laboratoryId)]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      switch (filter) {
        case 'active': return !l.isSuspended && l.subscription?.status === 'Active';
        case 'overdue': return !l.isSuspended && isOverdue(l);
        case 'pending': return l.pendingPaymentCount > 0;
        case 'suspended': return l.isSuspended;
        case 'nosub': return !l.subscription;
        default: return true;
      }
    });
  }, [labs, search, filter]);

  // Optimistically flips a boolean lab field and saves it, rolling back on failure.
  const toggleLabFlag = async (lab, field, save, label) => {
    const next = !lab[field];
    setBusy(lab.laboratoryId, true);
    updateLab(lab.laboratoryId, { [field]: next });
    try {
      await save(lab.laboratoryId, next);
      toast.success(`${label} ${next ? 'enabled' : 'disabled'} for ${lab.laboratoryName}`);
    } catch (error) {
      console.error(`Failed to update ${label}:`, error);
      updateLab(lab.laboratoryId, { [field]: !next });
      toast.error(`Failed to update ${label.toLowerCase()}`);
    } finally {
      setBusy(lab.laboratoryId, false);
    }
  };

  const handleToggleNotifications = (lab) =>
    toggleLabFlag(lab, 'notificationsAllowed', superAdminService.setNotificationsAllowed, 'Notifications');

  const handleTogglePush = (lab) =>
    toggleLabFlag(lab, 'pushAllowed', superAdminService.setPushAllowed, 'Push notifications');

  const confirmSuspension = async () => {
    const lab = suspendTarget;
    const next = !lab.isSuspended;
    setSuspendTarget(null);
    setBusy(lab.laboratoryId, true);
    try {
      await superAdminService.setSuspended(lab.laboratoryId, next);
      updateLab(lab.laboratoryId, { isSuspended: next });
      toast.success(`${lab.laboratoryName} ${next ? 'suspended' : 'reactivated'}`);
    } catch (error) {
      console.error('Failed to update suspension:', error);
      toast.error('Failed to update account status');
    } finally {
      setBusy(lab.laboratoryId, false);
    }
  };

  const openPayments = async (lab) => {
    setPaymentsLabId(lab.laboratoryId);
    setPayments([]);
    setPaymentsLoading(true);
    try {
      setPayments(await superAdminService.getPayments(lab.laboratoryId));
    } catch (error) {
      console.error('Failed to load payments:', error);
      toast.error('Failed to load payment history');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handlePaymentStatus = async (payment, status) => {
    setBusyPaymentId(payment.id);
    try {
      await superAdminService.setPaymentStatus(payment.id, status);
      setPayments((prev) => prev.map((p) => (p.id === payment.id ? { ...p, status } : p)));
      toast.success(`Payment ${status.toLowerCase()}`);
      // Verifying moves the lab's next due date forward and changes its
      // pending count, so refresh the list behind the dialog.
      fetchLabs();
    } catch (error) {
      console.error('Failed to update payment:', error);
      toast.error('Failed to update payment');
    } finally {
      setBusyPaymentId(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/');
  };

  const renderSubscriptionStatus = (lab) => {
    if (!lab.subscription) return <StatusPill tone="muted">No subscription</StatusPill>;
    if (lab.subscription.status !== 'Active') return <StatusPill tone="muted">{lab.subscription.status}</StatusPill>;
    if (isOverdue(lab)) return <StatusPill tone="warn">Overdue</StatusPill>;
    return <StatusPill tone="ok">Active</StatusPill>;
  };

  const paymentTone = (status) => (status === 'Verified' ? 'ok' : status === 'Rejected' ? 'danger' : 'warn');

  return (
    <div className="sa-page common-FontFamily">
      <header className="sa-topbar">
        <div>
          <h1 className="sa-title">Super Admin</h1>
          <span className="sa-subtitle">All laboratories, subscriptions and payments</span>
        </div>
        <div className="sa-topbar-actions">
          <Tooltip title="Refresh">
            <IconButton onClick={fetchLabs} disabled={loading}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddLab(true)} className="sa-add-lab">
            Add laboratory
          </Button>
          <Button startIcon={<LogoutIcon />} onClick={handleLogout} className="sa-logout">Logout</Button>
        </div>
      </header>

      <section className="sa-stats">
        <button type="button" className={`sa-stat ${filter === 'all' ? 'selected' : ''}`} onClick={() => setFilter('all')}>
          <span className="sa-stat-value">{stats.total}</span>
          <span className="sa-stat-label">Laboratories</span>
        </button>
        <button type="button" className={`sa-stat ${filter === 'active' ? 'selected' : ''}`} onClick={() => setFilter('active')}>
          <span className="sa-stat-value">{stats.active}</span>
          <span className="sa-stat-label">Active subscriptions</span>
        </button>
        <button type="button" className={`sa-stat ${filter === 'overdue' ? 'selected' : ''}`} onClick={() => setFilter('overdue')}>
          <span className="sa-stat-value sa-warn-text">{stats.overdue}</span>
          <span className="sa-stat-label">Payment overdue</span>
        </button>
        <button type="button" className={`sa-stat ${filter === 'pending' ? 'selected' : ''}`} onClick={() => setFilter('pending')}>
          <span className="sa-stat-value sa-accent-text">{stats.pending}</span>
          <span className="sa-stat-label">Payments to verify</span>
        </button>
        <button type="button" className={`sa-stat ${filter === 'suspended' ? 'selected' : ''}`} onClick={() => setFilter('suspended')}>
          <span className="sa-stat-value sa-danger-text">{stats.suspended}</span>
          <span className="sa-stat-label">Suspended</span>
        </button>
      </section>

      <section className="sa-card">
        <div className="sa-toolbar">
          <div className="sa-search">
            <SearchIcon fontSize="small" />
            <input
              type="text"
              placeholder="Search by name, email, phone or ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value)} className="sa-filter">
            {FILTERS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
          </Select>
        </div>

        {loading ? (
          <div className="sa-empty"><CircularProgress size={28} /></div>
        ) : visibleLabs.length === 0 ? (
          <div className="sa-empty">No laboratories match.</div>
        ) : (
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Laboratory</th>
                  <th>Users</th>
                  <th>Plan</th>
                  <th>Subscription</th>
                  <th>Next due</th>
                  <th>Payments</th>
                  <th>Notifications</th>
                  <th>Push</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {visibleLabs.map((lab) => {
                  const busy = busyIds.includes(lab.laboratoryId);
                  return (
                    <tr key={lab.laboratoryId} className={lab.isSuspended ? 'sa-row-suspended' : ''}>
                      <td>
                        <div className="sa-lab-name">{lab.laboratoryName}</div>
                        <div className="sa-lab-meta">
                          #{lab.laboratoryId}
                          {lab.laboratoryEmail && <> · {lab.laboratoryEmail}</>}
                          {lab.laboratoryContactNumber && <> · {lab.laboratoryContactNumber}</>}
                        </div>
                      </td>
                      <td className="sa-num">{lab.userCount}</td>
                      <td>
                        {lab.subscription ? (
                          <>
                            <div>{formatAmount(lab.subscription.amount)}</div>
                            <div className="sa-lab-meta">{lab.subscription.billingCycle} · since {formatDate(lab.subscription.startDate)}</div>
                          </>
                        ) : '—'}
                      </td>
                      <td>{renderSubscriptionStatus(lab)}</td>
                      <td className={isOverdue(lab) ? 'sa-warn-text' : ''}>{formatDate(lab.subscription?.nextDueDate)}</td>
                      <td>
                        <Button
                          size="small"
                          className="sa-link-button"
                          startIcon={<ReceiptLongIcon fontSize="small" />}
                          onClick={() => openPayments(lab)}
                        >
                          History
                          {lab.pendingPaymentCount > 0 && <span className="sa-count">{lab.pendingPaymentCount}</span>}
                        </Button>
                        <div className="sa-lab-meta">
                          {lab.lastPaymentAt ? `Last ${formatDate(lab.lastPaymentAt)}` : 'No payments yet'}
                        </div>
                      </td>
                      <td>
                        <Tooltip
                          title={
                            lab.notificationsAllowed && !lab.notificationsEnabledByLab
                              ? 'Allowed, but the lab has switched notifications off in its own settings'
                              : ''
                          }
                        >
                          <span className="sa-switch">
                            <Switch
                              size="small"
                              checked={lab.notificationsAllowed}
                              disabled={busy}
                              onChange={() => handleToggleNotifications(lab)}
                            />
                            <span className="sa-lab-meta">
                              {lab.notificationsAllowed ? (lab.notificationsEnabledByLab ? 'On' : 'Off by lab') : 'Off'}
                            </span>
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          title={
                            lab.notificationsAllowed
                              ? 'Phone / desktop alerts. In-app bell notifications are not affected.'
                              : 'Turn notifications on first - nothing is sent while they are off'
                          }
                        >
                          <span className="sa-switch">
                            <Switch
                              size="small"
                              checked={lab.notificationsAllowed && lab.pushAllowed}
                              disabled={busy || !lab.notificationsAllowed}
                              onChange={() => handleTogglePush(lab)}
                            />
                            <span className="sa-lab-meta">
                              {lab.notificationsAllowed && lab.pushAllowed ? 'On' : 'Off'}
                            </span>
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <div className="sa-account">
                          {lab.isSuspended ? <StatusPill tone="danger">Suspended</StatusPill> : <StatusPill tone="ok">Active</StatusPill>}
                          <Button
                            size="small"
                            variant="outlined"
                            color={lab.isSuspended ? 'primary' : 'error'}
                            disabled={busy}
                            onClick={() => setSuspendTarget(lab)}
                            className="sa-account-button"
                          >
                            {lab.isSuspended ? 'Reactivate' : 'Suspend'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AddLabDialog
        open={showAddLab}
        onClose={() => setShowAddLab(false)}
        onCreated={() => {
          setShowAddLab(false);
          fetchLabs();
        }}
      />

      {/* Suspend / reactivate confirmation */}
      <Dialog open={Boolean(suspendTarget)} onClose={() => setSuspendTarget(null)} maxWidth="xs" fullWidth>
        {suspendTarget && (
          <>
            <DialogTitle className="sa-dialog-title">
              {suspendTarget.isSuspended ? 'Reactivate' : 'Suspend'} {suspendTarget.laboratoryName}?
            </DialogTitle>
            <DialogContent className="common-FontSize-small">
              {suspendTarget.isSuspended
                ? 'Users of this laboratory will be able to log in and use the system again.'
                : `All ${suspendTarget.userCount} user(s) of this laboratory will be logged out and blocked from logging in until you reactivate it. Their data is kept.`}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSuspendTarget(null)}>Cancel</Button>
              <Button
                variant="contained"
                color={suspendTarget.isSuspended ? 'primary' : 'error'}
                onClick={confirmSuspension}
              >
                {suspendTarget.isSuspended ? 'Reactivate' : 'Suspend'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Payment history */}
      <Dialog open={Boolean(paymentsLab)} onClose={() => setPaymentsLabId(null)} maxWidth="md" fullWidth>
        {paymentsLab && (
          <>
            <DialogTitle className="sa-dialog-title">
              Payment history · {paymentsLab.laboratoryName}
              <IconButton onClick={() => setPaymentsLabId(null)} className="sa-dialog-close"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
              <div className="sa-payments-summary">
                <span>Plan: <strong>{paymentsLab.subscription ? `${formatAmount(paymentsLab.subscription.amount)} / ${paymentsLab.subscription.billingCycle}` : 'None'}</strong></span>
                <span>Next due: <strong>{formatDate(paymentsLab.subscription?.nextDueDate)}</strong></span>
                <span>Total verified: <strong>{formatAmount(paymentsLab.totalVerifiedAmount)}</strong></span>
              </div>
              {paymentsLoading ? (
                <div className="sa-empty"><CircularProgress size={24} /></div>
              ) : payments.length === 0 ? (
                <div className="sa-empty">No payments submitted yet.</div>
              ) : (
                <div className="sa-table-wrap">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Amount</th>
                        <th>Transaction</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td>{months[p.billingMonth - 1]} {p.billingYear}</td>
                          <td className="sa-num">{formatAmount(p.amount)}</td>
                          <td>
                            <div>{p.transactionMessage || '—'}</div>
                            {p.transactionReference && <div className="sa-lab-meta">Ref: {p.transactionReference}</div>}
                            {p.notes && <div className="sa-lab-meta">{p.notes}</div>}
                          </td>
                          <td>{formatDate(p.createdAt)}</td>
                          <td><StatusPill tone={paymentTone(p.status)}>{p.status}</StatusPill></td>
                          <td>
                            {p.status === 'Pending' && (
                              <div className="sa-payment-actions">
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={busyPaymentId === p.id}
                                  onClick={() => handlePaymentStatus(p, 'Verified')}
                                >
                                  Verify
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  disabled={busyPaymentId === p.id}
                                  onClick={() => handlePaymentStatus(p, 'Rejected')}
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
}
